import React from 'react';
import { Box, styled } from '@mui/material';
import ShimmerEffect from './ShimmerEffect';

// Generated by Copilot

interface ShimmerDataGridProps {
  /** Number of rows to show */
  rows?: number;
  /** Number of columns to show */
  columns?: number;
  /** Column widths array */
  columnWidths?: string[];
  /** Show header */
  showHeader?: boolean;
  /** Show checkboxes */
  showCheckboxes?: boolean;
  /** Show actions column */
  showActions?: boolean;
  /** Show pagination */
  showPagination?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Height of each row */
  rowHeight?: string | number;
  /** Header height */
  headerHeight?: string | number;
}

const DataGridContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  overflow: 'hidden',
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#fafafa',
  borderBottom: '1px solid #e0e0e0',
  minHeight: '56px',
  padding: '0 16px',
}));

const DataRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid #f0f0f0',
  minHeight: '52px',
  padding: '0 16px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

const Cell = styled(Box)<{ width?: string }>(({ width }) => ({
  flex: width ? `0 0 ${width}` : '1',
  padding: '0 8px',
  display: 'flex',
  alignItems: 'center',
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 16px',
  borderTop: '1px solid #e0e0e0',
  backgroundColor: '#fafafa',
  minHeight: '52px',
}));

const ShimmerDataGrid: React.FC<ShimmerDataGridProps> = ({
  rows = 10,
  columns = 7,
  columnWidths = ['60px', '140px', '140px', '120px', '180px', '140px', '180px', '70px'],
  showHeader = true,
  showCheckboxes = true,
  showActions = true,
  showPagination = true,
  className = '',
  rowHeight = '52px',
  headerHeight = '56px',
}) => {
  // Default column widths if not provided
  const defaultWidths = Array.from({ length: columns }, (_, index) => {
    if (index === 0 && showCheckboxes) return '60px';
    if (index === columns - 1 && showActions) return '70px';
    return `${120 + Math.random() * 60}px`;
  });

  const actualWidths = columnWidths.length >= columns ? columnWidths : defaultWidths;

  const renderHeaderCell = (index: number) => {
    if (index === 0 && showCheckboxes) {
      return (
        <Cell key={index} width={actualWidths[index]}>
          <ShimmerEffect variant="rectangular" width="18px" height="18px" />
        </Cell>
      );
    }
    
    if (index === columns - 1 && showActions) {
      return (
        <Cell key={index} width={actualWidths[index]}>
          <ShimmerEffect width="60px" height="16px" />
        </Cell>
      );
    }

    return (
      <Cell key={index} width={actualWidths[index]}>
        <ShimmerEffect width="80%" height="16px" />
      </Cell>
    );
  };

  const renderDataCell = (rowIndex: number, cellIndex: number) => {
    if (cellIndex === 0 && showCheckboxes) {
      return (
        <Cell key={cellIndex} width={actualWidths[cellIndex]}>
          <ShimmerEffect variant="rectangular" width="18px" height="18px" />
        </Cell>
      );
    }
    
    if (cellIndex === columns - 1 && showActions) {
      return (
        <Cell key={cellIndex} width={actualWidths[cellIndex]}>
          <ShimmerEffect variant="circular" width="24px" height="24px" />
        </Cell>
      );
    }

    // Vary the width for more realistic look
    const widthVariants = ['60%', '70%', '80%', '90%', '100%'];
    const randomWidth = widthVariants[Math.floor(Math.random() * widthVariants.length)];

    return (
      <Cell key={cellIndex} width={actualWidths[cellIndex]}>
        {/* Generated by Copilot */}
        <ShimmerEffect 
          width={randomWidth} 
          height="16px"
        />
      </Cell>
    );
  };

  return (
    <DataGridContainer className={`shimmer-data-grid ${className}`}>
      {/* Header Row */}
      {showHeader && (
        <HeaderRow sx={{ height: headerHeight }}>
          {Array.from({ length: columns }).map((_, index) => renderHeaderCell(index))}
        </HeaderRow>
      )}

      {/* Data Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <DataRow key={rowIndex} sx={{ height: rowHeight }}>
          {Array.from({ length: columns }).map((_, cellIndex) => 
            renderDataCell(rowIndex, cellIndex)
          )}
        </DataRow>
      ))}

      {/* Pagination */}
      {showPagination && (
        <PaginationContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShimmerEffect width="150px" height="16px" />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShimmerEffect width="60px" height="16px" />
            <ShimmerEffect width="120px" height="32px" borderRadius="4px" />
            <ShimmerEffect width="80px" height="16px" />
          </Box>
        </PaginationContainer>
      )}
    </DataGridContainer>
  );
};

export default ShimmerDataGrid;

// Usage examples:

/**
 * Basic Usage Examples:
 * 
 * // Basic data grid shimmer (matches your device table)
 * <ShimmerDataGrid />
 * 
 * // Custom configuration
 * <ShimmerDataGrid 
 *   rows={8}
 *   columns={6}
 *   columnWidths={['60px', '140px', '140px', '120px', '180px', '70px']}
 * />
 * 
 * // Without checkboxes
 * <ShimmerDataGrid showCheckboxes={false} />
 * 
 * // Without actions
 * <ShimmerDataGrid showActions={false} />
 * 
 * // Compact version
 * <ShimmerDataGrid 
 *   rows={5}
 *   rowHeight="40px"
 *   headerHeight="48px"
 * />
 * 
 * // Without pagination
 * <ShimmerDataGrid showPagination={false} />
 */
